// Checkout without double orders (MS-03/MS-07). Before an order is sent, the exact request and its idempotency key
// are saved in the browser as the "pending checkout". It is removed only when the outcome is known:
//   - the shop answered with the order (201, or 200 for a replayed retry), or
//   - the shop itself refused it with one of its definitive answers (DEFINITIVE below: invalid, sold out, unknown
//     macaron), which it gives only when no order exists for the key: the API returns an existing keyed order before
//     any other check (MS-10).
// Any other failure (network error, lost response, 5xx, a 401/403/429 from a proxy or login) leaves it pending, also across closing the drawer or
// reloading the page; the customer can then only retry that same request with the same key (the shop returns the
// order if the first attempt went through) or discard it. A new checkout cannot start while one is pending.
import { newCheckoutKey } from './shop';

const KEY = 'macaron-pending-checkout';

export function loadPending(storage) {
  try {
    const raw = storage && storage.getItem(KEY);
    const p = raw ? JSON.parse(raw) : null;
    return p && typeof p.key === 'string' && p.request && typeof p.request === 'object' ? p : null;
  } catch {
    return null;
  }
}

function savePending(storage, pending) {
  try {
    if (!storage) return;
    if (pending) storage.setItem(KEY, JSON.stringify(pending));
    else storage.removeItem(KEY);
  } catch {
    /* not kept: retries in this page still reuse the key held in memory */
  }
}

const DEFINITIVE = ['invalid_request', 'invalid_json', 'too_large', 'out_of_stock', 'unknown_product'];
export const refused = (err) => Boolean(err && err.status >= 400 && err.status < 500 && DEFINITIVE.includes(err.code));

// Sends `pending` (a fresh one from `request` when none is given). place(request, key) -> order.
// Returns { order } on success, { refused: err } when the shop said no, { unknown: err, pending } otherwise.
// onAttempt(attempt) runs before anything is sent, so the caller holds the attempt even if this page part goes away.
export async function submitCheckout({ storage, place, request, pending, makeKey = newCheckoutKey, onAttempt = () => {} }) {
  const attempt = pending || { key: makeKey(), request };
  savePending(storage, attempt);
  onAttempt(attempt);
  try {
    const order = await place(attempt.request, attempt.key);
    savePending(storage, null);
    return { order };
  } catch (err) {
    if (refused(err)) {
      savePending(storage, null);
      return { refused: err };
    }
    return { unknown: err, pending: attempt };
  }
}

export const discardPending = (storage) => savePending(storage, null);
